<template>
  <footer class="app-footer">
    <div class="footer-container">
      <!-- Main Footer Content - Single Row on Desktop -->
      <div class="footer-main">
        <!-- League Branding - Compact -->
        <div class="footer-brand">
          <div class="brand-icon">
            <Icon name="mdi:pool" />
          </div>
          <div class="brand-content">
            <h3>The Players League DFW</h3>
            <p class="tagline">Professional Pool League • Dallas-Fort Worth</p>
          </div>
        </div>

        <!-- Quick Links - Horizontal on Desktop -->
        <nav class="footer-nav">
          <h4>Quick Links</h4>
          <div class="nav-grid">
            <nuxt-link to="/">Home</nuxt-link>
            <nuxt-link to="/standings">Standings</nuxt-link>
            <nuxt-link to="/league-format">Format</nuxt-link>
            <nuxt-link to="/contact">Contact</nuxt-link>
          </div>
        </nav>

        <!-- Connect Section - Compact -->
        <div class="footer-connect">
          <h4>Connect</h4>
          <a 
            href="https://www.facebook.com/groups/1491189844590894" 
            target="_blank" 
            rel="noopener noreferrer"
            class="facebook-link"
          >
            <Icon name="mdi:facebook" />
            <span>Join Our Facebook Group</span>
          </a>
          <div class="quick-info">
            <span><Icon name="mdi:map-marker" /> DFW Area</span>
            <span><Icon name="mdi:calendar" /> Tue, Wed, Sat</span>
          </div>
        </div>
      </div>

      <!-- Footer Bottom - Copyright -->
      <div class="footer-bottom">
        <p class="copyright">&copy; {{ currentYear }} The Players League DFW</p>
        <p class="credits">
          Built with <Icon name="mdi:heart" class="heart" /> by 
          <a href="https://casebycasestudios.com" target="_blank" rel="noopener noreferrer">
            Case by Case Studios
          </a>
        </p>
      </div>
    </div>
  </footer>
</template>

<script setup>
const currentYear = new Date().getFullYear()
</script>

<style scoped>
.app-footer {
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.97) 0%, rgba(30, 58, 138, 0.97) 100%);
  border-top: 2px solid var(--gold);
  position: relative;
  overflow: hidden;
  width: 100%;
}

.footer-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 2;
  width: 100%;
}

/* Main Footer Content */
.footer-main {
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr;
  gap: 48px;
  align-items: start;
  padding: 40px 0 32px;
  border-bottom: 1px solid rgba(255, 184, 28, 0.15);
}

/* Brand Section - Compact */
.footer-brand {
  display: flex;
  align-items: center;
  gap: 16px;
}

.brand-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--gold), var(--light-gold));
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--navy-blue);
  font-size: 1.5rem;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(255, 184, 28, 0.3);
}

.brand-content h3 {
  color: var(--gold);
  font-size: 1.2rem;
  font-family: var(--font-heading);
  font-weight: 700;
  margin: 0 0 4px 0;
  line-height: 1.2;
}

.tagline {
  color: var(--cream);
  font-size: 0.85rem;
  margin: 0;
  opacity: 0.8;
}

/* Navigation - Compact Grid */
.footer-nav h4,
.footer-connect h4 {
  color: var(--gold);
  font-size: 0.95rem;
  font-family: var(--font-heading);
  font-weight: 600;
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.nav-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.nav-grid a {
  color: var(--cream);
  text-decoration: none;
  font-size: 0.9rem;
  padding: 6px 0;
  transition: all 0.2s ease;
  opacity: 0.85;
  position: relative;
}

.nav-grid a::before {
  content: '›';
  color: var(--gold);
  margin-right: 6px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.nav-grid a:hover {
  color: var(--gold);
  opacity: 1;
  padding-left: 8px;
}

.nav-grid a:hover::before {
  opacity: 1;
}

/* Connect Section - Streamlined */
.footer-connect {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.facebook-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(66, 103, 178, 0.15);
  border: 1px solid rgba(66, 103, 178, 0.3);
  border-radius: 10px;
  color: var(--cream);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.facebook-link:hover {
  background: rgba(66, 103, 178, 0.25);
  border-color: rgba(66, 103, 178, 0.5);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(66, 103, 178, 0.2);
}

.facebook-link svg {
  font-size: 1.3rem;
  color: #4267B2;
}

.quick-info {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 0.85rem;
  color: var(--cream);
  opacity: 0.8;
}

.quick-info span {
  display: flex;
  align-items: center;
  gap: 6px;
}

.quick-info svg {
  color: var(--gold);
  font-size: 0.95rem;
}

/* Footer Bottom - Minimal */
.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  font-size: 0.85rem;
  color: var(--cream);
  opacity: 0.75;
  gap: 16px;
  flex-wrap: wrap;
}

.copyright {
  margin: 0;
}

.credits {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.heart {
  color: #ef4444;
  font-size: 0.75rem;
}

.credits a {
  color: var(--gold);
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s ease;
}

.credits a:hover {
  color: var(--light-gold);
  text-decoration: underline;
}

/* Tablet Layout */
@media (max-width: 1024px) {
  .footer-main {
    grid-template-columns: 1fr 1fr;
    gap: 32px;
    padding: 32px 0 24px;
  }
  
  .footer-brand {
    grid-column: 1 / -1;
    justify-content: center;
  }
}

/* Mobile Layout - Stacked */
@media (max-width: 768px) {
  .footer-container {
    padding: 0 16px;
  }
  
  .footer-main {
    grid-template-columns: 1fr;
    gap: 24px;
    padding: 28px 0 20px;
    text-align: center;
  }
  
  .footer-brand {
    flex-direction: column;
    gap: 12px;
  }
  
  .brand-content h3 {
    font-size: 1.1rem;
  }
  
  .tagline {
    font-size: 0.8rem;
  }
  
  .nav-grid {
    grid-template-columns: 1fr;
    max-width: 200px;
    margin: 0 auto;
  }
  
  .nav-grid a:hover {
    padding-left: 0;
  }
  
  .footer-connect {
    align-items: center;
  }
  
  .facebook-link {
    max-width: 280px;
    width: 100%;
    justify-content: center;
  }
  
  .quick-info {
    justify-content: center;
    font-size: 0.8rem;
  }
  
  .footer-bottom {
    flex-direction: column;
    text-align: center;
    padding: 16px 0;
    gap: 8px;
  }
}

/* Small Mobile - Ultra Compact */
@media (max-width: 480px) {
  .footer-container {
    padding: 0 12px;
  }
  
  .footer-main {
    gap: 20px;
    padding: 24px 0 16px;
  }
  
  .brand-icon {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
  
  .brand-content h3 {
    font-size: 1rem;
  }
  
  .footer-nav h4,
  .footer-connect h4 {
    font-size: 0.85rem;
  }
  
  .facebook-link {
    padding: 10px 14px;
    font-size: 0.85rem;
  }
  
  .quick-info {
    flex-direction: column;
    gap: 8px;
  }
  
  .footer-bottom {
    font-size: 0.75rem;
    padding: 14px 0;
  }
}
</style>
